import { useAssetDetailsContext } from "@/pages/asset-details/context/assetDetailsContext";
import { removeAttachmentLink } from "@/pages/asset-details/repositories/AssetDetailsRepositorie";
import type { GridApi } from "ag-grid-community";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const useAssetAttachmentsGridActionsAndButtons = ({
	gridRef,
	searchAssetAttachments,
	setIsRemoveLinkAssetAttachmentsLoading,
	setRemoveLinkAssetAttachmentsLoadingError,
	includeDescendants,
}: {
	gridRef: GridApi | null;
	searchAssetAttachments: Function;
	setIsRemoveLinkAssetAttachmentsLoading: (isLoading: boolean) => void;
	setRemoveLinkAssetAttachmentsLoadingError: (err: string) => void;
	includeDescendants: boolean;
}) => {
	const {
		state: {
			assetSearchId,
			listAllAttachments,
			listAssetAttachments,
			rowSelected,
		},
		actions: {
			setFirstTime,
			setAttachmentSearch,
			setDataAttachmentSelected,
			setListAssetAttachments,
			setListAttachmentSelected,
		},
	} = useAssetDetailsContext();
	const [searchParams] = useSearchParams();
	const [refreshTrigger, setRefreshTrigger] = useState(1);
	const isMountRef = useRef(true);

	const [addButton, setAddButton] = useState<boolean>(true);
	const [deleteButton, setDeleteButton] = useState<boolean>(false);
	const [saveButton, setSaveButton] = useState<boolean>(false);
	const [discardButton, setDiscardButton] = useState<boolean>(false);
	const [deleteOpen, setDeleteOpen] = useState(false);

	const refresh = useCallback(() => {
		if (!isMountRef.current) {
			setFirstTime(true);
			setSaveButton(false);
			setDiscardButton(false);
			setAddButton(true);
			setDeleteButton(false);
			setDeleteOpen(false);
			setAttachmentSearch("");
			setDataAttachmentSelected(undefined);
			searchAssetAttachments(includeDescendants);
		}
	}, [searchParams, assetSearchId, includeDescendants]);

	const addRow = useCallback(() => {
		setAddButton(false);
		setSaveButton(true);
		setDiscardButton(true);
		setDeleteButton(false);
		const newData = { id: -1, AssetId: assetSearchId };
		const currentData = listAssetAttachments ? listAssetAttachments : [];
		const data = [newData, ...currentData];
		setListAssetAttachments(data);
	}, [listAssetAttachments, assetSearchId]);

	const removeSelected = useCallback(async () => {
		setIsRemoveLinkAssetAttachmentsLoading(true);
		const selectedRowNodes = gridRef?.getSelectedNodes();
		try {
			if (selectedRowNodes?.length) {
				const removePromises = selectedRowNodes.map(async (attachmentLink) => {
					const removeAttachment = await removeAttachmentLink(
						assetSearchId as any,
						attachmentLink.data.UploadFileId,
					);
					if (!removeAttachment.ok) {
						setRemoveLinkAssetAttachmentsLoadingError(removeAttachment.message);
						throw new Error(removeAttachment.message);
					}
					return removeAttachment;
				});

				await Promise.all(removePromises);
			} else if (rowSelected) {
				const removeAttachment = await removeAttachmentLink(
					assetSearchId as any,
					rowSelected.UploadFileId,
				);
				if (!removeAttachment.ok) {
					setRemoveLinkAssetAttachmentsLoadingError(removeAttachment.message);
					setIsRemoveLinkAssetAttachmentsLoading(false);
					return;
				}
			}

			setIsRemoveLinkAssetAttachmentsLoading(false);
			setRefreshTrigger((prev) => prev + 1);
		} catch (error) {
			setIsRemoveLinkAssetAttachmentsLoading(false);
		}
	}, [listAllAttachments, assetSearchId, refresh, gridRef, rowSelected]);

	useEffect(() => {
		refresh();
	}, [refreshTrigger]);

	useEffect(() => {
		isMountRef.current = false;
	}, []);

	return {
		refresh,
		addRow,
		removeSelected,
		addButton,
		deleteButton,
		saveButton,
		discardButton,
		deleteOpen,
		setSaveButton,
		setDiscardButton,
		setAddButton,
		setDeleteButton,
		setDeleteOpen,
	};
};
